// apps/codeframe/src/chrome/timeline/TrackList.tsx
import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Layers, Clapperboard } from 'lucide-react';
import { useCanvasStore } from '../../store/canvas-store';
import { lottieElementsOf } from '../../lottie/scan';
import { getLottieHandles } from '../../lottie/registry';
import { shortId } from '../dropDetect';
import { defaultValueForProp } from './timeMap';
import { ALL_PROPS } from './useMotionScope';
import { NodePicker } from './NodePicker';
import { ChannelValueField } from './ChannelValueField';
import { hoverHighlight, clearHoverHighlight, selectTrackTarget } from './useTrackHighlight';
import { startPickOnCanvas } from './pickOnCanvas';
import type { MotionTimeline, MotionProp } from '../../store/types';

export const ROW_H = 22; // px; shared with KeyframeLanes

export function TrackList({ frameId, timeline }: { frameId: string; timeline: MotionTimeline }) {
  const dispatch = useCanvasStore((s) => s.dispatch);
  const frame = useCanvasStore((s) => s.doc.frames.find((f) => f.id === frameId));
  const lotties = frame && 'html' in frame ? lottieElementsOf((frame as { html: string }).html) : [];
  const handles = getLottieHandles(frameId);
  // 'idle' | 'canvas' (crosshair pick on canvas) | 'list' (NodePicker dropdown)
  const [pickMode, setPickMode] = useState<'idle' | 'canvas' | 'list'>('idle');
  const stopPick = useRef<(() => void) | null>(null);

  const addTrack = (cfId: string, label: string) => {
    dispatch({ t: 'motion.addTrack', frameId, trackId: shortId(), layerId: shortId(), target: cfId, label });
    setPickMode('idle');
  };

  // Drive the crosshair canvas picker for the duration of 'canvas' mode.
  useEffect(() => {
    if (pickMode !== 'canvas') return;
    stopPick.current = startPickOnCanvas(frameId, addTrack);
    return () => {
      stopPick.current?.();
      stopPick.current = null;
    };
    // addTrack is stable enough for this scope (frameId/dispatch); re-run only on mode/frame change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickMode, frameId]);

  const addLayer = (trackId: string) =>
    dispatch({ t: 'motion.addLayer', frameId, trackId, layerId: shortId(), blend: 'add' });

  const removeTrack = (trackId: string) => dispatch({ t: 'motion.removeTrack', frameId, trackId });

  const toggleBlend = (trackId: string, layerId: string, cur: 'replace' | 'add') =>
    dispatch({ t: 'motion.setLayerBlend', frameId, trackId, layerId, blend: cur === 'add' ? 'replace' : 'add' });

  // Seeding a channel: setKeyframe at t=0 creates the channel implicitly.
  const addProp = (trackId: string, layerId: string, prop: MotionProp) =>
    dispatch({
      t: 'motion.setKeyframe',
      frameId,
      trackId,
      layerId,
      prop,
      kf: { t: 0, value: defaultValueForProp(prop), ease: 'easeInOut' },
    });

  return (
    <div className="relative w-56 shrink-0 overflow-y-auto border-r border-[var(--color-chip-border)] text-xs font-[family-name:var(--font-sans)]">
      {lotties.map((el, i) => (
        <div
          key={el.cfNodeId || `lottie-${i}`}
          className="flex cursor-pointer items-center gap-1 border-b border-[var(--color-chip-border)] px-3 text-[var(--color-text)]"
          style={{ height: ROW_H }}
          title="Lottie clip — edit via agent or the Animation panel"
          onMouseEnter={() => hoverHighlight(frameId, el.cfNodeId)}
          onMouseLeave={clearHoverHighlight}
          onClick={() => selectTrackTarget(frameId, el.cfNodeId)}
        >
          <Clapperboard size={12} className="shrink-0 text-[var(--color-text-muted)]" />
          <span className="truncate">lottie {el.cfNodeId}</span>
          <span className="ml-auto shrink-0 opacity-60">
            {((handles[i]?.durationMs ?? 0) / 1000).toFixed(1)}s{el.loop ? ' loop' : ''}
          </span>
        </div>
      ))}
      {timeline.tracks.map((track) => (
        <div key={track.id} className="border-b border-[var(--color-chip-border)]">
          {/* track header */}
          <div
            className="flex h-6 cursor-pointer items-center gap-1 bg-[var(--color-menu-hover)] px-2"
            onMouseEnter={() => hoverHighlight(frameId, track.target)}
            onMouseLeave={clearHoverHighlight}
            onClick={() => selectTrackTarget(frameId, track.target)}
          >
            <span className="flex-1 truncate" title={track.target}>
              {track.label ?? track.target}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); addLayer(track.id); }}
              className="hover:text-[var(--color-accent)]"
              title="Add layer"
            >
              <Layers size={12} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); removeTrack(track.id); }}
              className="hover:text-[var(--color-danger)]"
              title="Remove track"
            >
              <Trash2 size={12} />
            </button>
          </div>
          {track.layers.map((layer, li) => (
            <div key={layer.id}>
              <div className="flex h-5 items-center gap-1 px-2 text-[var(--color-text-muted)]">
                <button
                  onClick={() => toggleBlend(track.id, layer.id, layer.blend)}
                  className={`rounded px-1 text-[9px] uppercase ${
                    layer.blend === 'add' ? 'bg-[var(--color-accent)] text-white' : 'border border-[var(--color-chip-border)]'
                  }`}
                  title="Toggle blend mode"
                >
                  {layer.blend}
                </button>
                <span>{li === 0 ? 'base' : `layer ${li}`}</span>
                <AddPropMenu onPick={(p) => addProp(track.id, layer.id, p)} existing={layer.channels.map((c) => c.prop)} />
              </div>
              {layer.channels.map((ch) => (
                <div key={ch.prop} className="flex items-center px-3 text-[var(--color-text)]" style={{ height: ROW_H }}>
                  <span className="truncate">{ch.prop}</span>
                  <ChannelValueField
                    frameId={frameId}
                    timeline={timeline}
                    trackId={track.id}
                    layerId={layer.id}
                    prop={ch.prop}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}

      {pickMode === 'canvas' ? (
        <div className="flex h-7 w-full items-center gap-1 px-2 text-[var(--color-text-muted)]">
          <span className="truncate">click an element on the canvas · </span>
          <button onClick={() => setPickMode('idle')} className="shrink-0 hover:text-[var(--color-text)]">
            esc
          </button>
          <span> · </span>
          <button onClick={() => setPickMode('list')} className="shrink-0 hover:text-[var(--color-accent)]">
            list
          </button>
        </div>
      ) : (
        <button
          onClick={() => setPickMode('canvas')}
          className="flex h-7 w-full items-center gap-1 px-2 text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)]"
        >
          <Plus size={12} /> add track
        </button>
      )}

      {pickMode === 'list' && (
        <NodePicker frameId={frameId} onPick={addTrack} onClose={() => setPickMode('idle')} />
      )}
    </div>
  );
}

function AddPropMenu({ onPick, existing }: { onPick: (p: MotionProp) => void; existing: MotionProp[] }) {
  const [open, setOpen] = useState(false);
  const avail = ALL_PROPS.filter((p) => !existing.includes(p));
  if (avail.length === 0) return null;
  return (
    <span className="relative ml-auto">
      <button onClick={() => setOpen((v) => !v)} className="hover:text-[var(--color-accent)]" title="Add property">
        <Plus size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-4 z-10 max-h-40 w-32 overflow-auto rounded border border-[var(--color-chip-border)] bg-[var(--color-surface)] shadow-[var(--shadow-2)]">
          {avail.map((p) => (
            <button
              key={p}
              onClick={() => {
                onPick(p);
                setOpen(false);
              }}
              className="block w-full px-2 py-0.5 text-left text-[var(--color-text)] hover:bg-[var(--color-menu-hover)]"
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
